/**
 * Cart Preorder Message
 * Shows a warning message when preorder and regular items are mixed
 * - preorder + regular = BLOCK checkout (original behavior)
 * - preorder2 + regular = WARNING only, checkout stays enabled
 */

console.log('📦 cart-preorder-message.js loaded');

window.CartPreorderMessage = (function() {
  const CONFIG = {
    PREORDER_TAG: 'preorder',
    PREORDER2_TAG: 'preorder2',
    CONTAINER_ID: 'cart-preorder-message-container',
    DATA_SCRIPT_ID: 'cart-preorder-data'
  };

  // Cache for product data to avoid redundant fetches
  const productCache = new Map();

  // Cache for preorder handles to avoid repeated API calls
  const preorderHandleCache = new Set();
  const preorder2HandleCache = new Set();

  /**
   * Get product data by handle with caching
   */
  async function getProductData(handle) {
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      productCache.set(handle, data);
      return data;
    } catch (error) {
      console.error('Error fetching product:', handle, error);
      productCache.set(handle, null);
      return null;
    }
  }

  /**
   * Get tag array from product tags
   */
  function getTagArray(tags) {
    if (!tags) return [];
    return Array.isArray(tags) ? tags : tags.split(',').map(t => t.trim());
  }

  /**
   * Check if product has a specific tag
   */
  function hasTag(tags, tag) {
    return getTagArray(tags).includes(tag);
  }

  /**
   * Extract handle from cart item
   */
  function getItemHandle(item) {
    if (item.handle) return item.handle;
    if (item.url) {
      const match = item.url.match(/\/products\/([^\/\?]+)/);
      if (match) return match[1];
    }
    return null;
  }

  /**
   * The auto-added free gift is invisible to this check.
   *
   * It is not a product the customer picked, so counting it as a regular item
   * made a cart of nothing but preorder products look mixed, and raised the
   * "cannot be combined" warning against a bottle the shop put in there itself.
   * Mirrored in cart-preorder-warning.liquid, which builds the same verdict
   * server-side — both have to skip it or the warning returns by the other route.
   */
  function isAutoGift(item) {
    return Boolean(item && item.properties && item.properties._auto_gift);
  }

  function cartItemsWithoutGift(cart) {
    return (cart && cart.items) ? cart.items.filter(item => !isAutoGift(item)) : [];
  }

  /**
   * Get server-side preorder data if available
   */
  function getServerPreorderData() {
    const dataScript = document.getElementById(CONFIG.DATA_SCRIPT_ID);
    if (!dataScript) return null;

    try {
      const data = JSON.parse(dataScript.textContent);
      console.log('📊 Server preorder data:', data);

      if (data.preorderItems) {
        data.preorderItems.split(',').forEach(handle => {
          if (handle.trim()) preorderHandleCache.add(handle.trim());
        });
      }
      if (data.preorder2Items) {
        data.preorder2Items.split(',').forEach(handle => {
          if (handle.trim()) preorder2HandleCache.add(handle.trim());
        });
      }

      return data;
    } catch (error) {
      console.error('Error parsing server preorder data:', error);
      return null;
    }
  }

  /**
   * Analyze cart and classify items
   * Returns { hasPreorder, hasPreorder2, hasRegular, hasPreorderMix, hasPreorder2Mix }
   */
  function classifyItems(items, getHandleType) {
    let hasPreorder = false;
    let hasPreorder2 = false;
    let hasRegular = false;

    for (const item of items) {
      const type = getHandleType(item);
      if (type === 'preorder2') {
        hasPreorder2 = true;
      } else if (type === 'preorder') {
        hasPreorder = true;
      } else {
        hasRegular = true;
      }
    }

    return {
      hasPreorder,
      hasPreorder2,
      hasRegular,
      // Original preorder + regular = block checkout
      hasPreorderMix: hasPreorder && hasRegular,
      // Preorder2 + regular = warning only (no block)
      hasPreorder2Mix: hasPreorder2 && hasRegular && !hasPreorder
    };
  }

  /**
   * Fast analysis using cached handles
   */
  function analyzeCartFast(cart) {
    const items = cartItemsWithoutGift(cart);
    if (!items.length) {
      return { hasPreorder: false, hasPreorder2: false, hasRegular: false, hasPreorderMix: false, hasPreorder2Mix: false };
    }

    return classifyItems(items, (item) => {
      const handle = getItemHandle(item);
      if (!handle) return 'regular';
      if (preorder2HandleCache.has(handle)) return 'preorder2';
      if (preorderHandleCache.has(handle)) return 'preorder';
      return 'regular';
    });
  }

  /**
   * Analyze cart via API (fallback)
   */
  async function analyzeCartFallback(cart) {
    const items = cartItemsWithoutGift(cart);
    if (!items.length) {
      return { hasPreorder: false, hasPreorder2: false, hasRegular: false, hasPreorderMix: false, hasPreorder2Mix: false };
    }

    const productDataPromises = items.map(item => {
      const handle = getItemHandle(item);
      return handle ? getProductData(handle) : Promise.resolve(null);
    });

    const productDataList = await Promise.all(productDataPromises);

    // Build a map of index -> type
    const itemTypes = productDataList.map((productData, i) => {
      if (!productData?.tags) return 'regular';

      // items, not cart.items: the list was filtered above, so an index into the
      // original array would line handles up against the wrong products.
      const handle = getItemHandle(items[i]);

      if (hasTag(productData.tags, CONFIG.PREORDER2_TAG)) {
        if (handle) preorder2HandleCache.add(handle);
        return 'preorder2';
      }
      if (hasTag(productData.tags, CONFIG.PREORDER_TAG)) {
        if (handle) preorderHandleCache.add(handle);
        return 'preorder';
      }
      return 'regular';
    });

    let hasPreorder = false, hasPreorder2 = false, hasRegular = false;
    for (const type of itemTypes) {
      if (type === 'preorder2') hasPreorder2 = true;
      else if (type === 'preorder') hasPreorder = true;
      else hasRegular = true;
    }

    return {
      hasPreorder,
      hasPreorder2,
      hasRegular,
      hasPreorderMix: hasPreorder && hasRegular,
      hasPreorder2Mix: hasPreorder2 && hasRegular && !hasPreorder
    };
  }

  /**
   * Get message text for original preorder conflict
   */
  function getMessageText() {
    const serverData = getServerPreorderData();
    if (serverData?.message) return serverData.message;
    if (window.theme?.strings?.['cart.preorder_conflict']) {
      return window.theme.strings['cart.preorder_conflict'];
    }
    return 'Your cart contains both preorder and regular items. Please remove and checkout separately.';
  }

  /**
   * Get message text for preorder2 warning
   */
  function getPreorder2MessageText() {
    const serverData = getServerPreorderData();
    if (serverData?.preorder2Message) return serverData.preorder2Message;
    if (window.theme?.strings?.['cart.preorder2_mixed_warning']) {
      return window.theme.strings['cart.preorder2_mixed_warning'];
    }
    return 'Your order contains pre-sale items. The full shipment will be dispatched once all items are available — please note this may result in extended waiting times.';
  }

  /**
   * Disable/enable checkout buttons based on cart state
   */
  function updateCheckoutButtons(hasMix) {
    if (hasMix) {
      sessionStorage.setItem('preorderCheckoutDisabled', 'true');
    } else {
      sessionStorage.removeItem('preorderCheckoutDisabled');
    }

    const checkoutButtons = document.querySelectorAll('a[href="/checkout"], button[name="checkout"], a[data-preorder-disabled="true"], button[data-preorder-disabled="true"]');

    checkoutButtons.forEach(button => {
      if (hasMix) {
        button.disabled = true;
        button.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
        button.setAttribute('aria-disabled', 'true');
        button.dataset.preorderDisabled = 'true';

        if (button.tagName === 'A' && button.href) {
          button.dataset.originalHref = button.href;
          button.removeAttribute('href');
        }

        button.style.cursor = 'not-allowed';
        button.style.opacity = '0.5';
        button.style.pointerEvents = 'none';

        console.log('🚫 Checkout button disabled due to mixed cart');
      } else {
        button.disabled = false;
        button.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
        button.removeAttribute('aria-disabled');
        button.removeAttribute('data-preorder-disabled');

        if (button.tagName === 'A' && button.dataset.originalHref) {
          button.href = button.dataset.originalHref;
          button.removeAttribute('data-original-href');
        }

        button.style.cursor = '';
        button.style.opacity = '';
        button.style.pointerEvents = '';

        console.log('✅ Checkout button enabled');
      }
    });
  }

  /**
   * Show original preorder conflict message (blocks checkout)
   */
  function showMessage() {
    const container = document.getElementById(CONFIG.CONTAINER_ID);
    if (!container) return;

    // Remove any preorder2 message first
    const existing2 = container.querySelector('.cart-preorder2-message');
    if (existing2) existing2.remove();

    const existing = container.querySelector('.cart-preorder-message');
    if (existing) return;

    const message = document.createElement('div');
    message.className = 'cart-preorder-message p-3 mb-3 bg-yellow-100 text-yellow-800 text-sm rounded';
    message.textContent = getMessageText();
    container.appendChild(message);
    console.log('✅ Preorder conflict message shown');

    updateCheckoutButtons(true);
  }

  /**
   * Show preorder2 warning message (does NOT block checkout)
   */
  function showPreorder2Message() {
    const container = document.getElementById(CONFIG.CONTAINER_ID);
    if (!container) return;

    // Don't show if original preorder message is active
    const existingConflict = container.querySelector('.cart-preorder-message');
    if (existingConflict) return;

    const existing = container.querySelector('.cart-preorder2-message');
    if (existing) return;

    const message = document.createElement('div');
    message.className = 'cart-preorder2-message p-3 mb-3 bg-blue-50 text-blue-800 text-sm rounded';
    message.textContent = getPreorder2MessageText();
    container.appendChild(message);
    console.log('✅ Preorder2 info message shown (checkout NOT blocked)');

    // Ensure checkout is NOT blocked
    updateCheckoutButtons(false);
  }

  /**
   * Hide all messages
   */
  function hideMessage() {
    const container = document.getElementById(CONFIG.CONTAINER_ID);
    if (!container) return;

    const message = container.querySelector('.cart-preorder-message');
    if (message) message.remove();

    const message2 = container.querySelector('.cart-preorder2-message');
    if (message2) message2.remove();

    updateCheckoutButtons(false);
  }

  /**
   * Update message based on cart analysis
   */
  async function updateMessage(cart) {
    console.log('🔍 Checking cart for preorder message', { itemCount: cart?.items?.length || 0 });

    try {
      let analysis;

      if (cart?.items?.length > 0) {
        console.log('📡 Using API analysis for accurate results');
        analysis = await analyzeCartFallback(cart);
      } else {
        analysis = { hasPreorder: false, hasPreorder2: false, hasRegular: false, hasPreorderMix: false, hasPreorder2Mix: false };
      }

      console.log('📊 Analysis:', analysis);

      if (analysis.hasPreorderMix) {
        // Original preorder + regular = block checkout
        showMessage();
      } else if (analysis.hasPreorder2Mix) {
        // Preorder2 + regular = warning only
        hideMessage();
        showPreorder2Message();
      } else {
        hideMessage();
      }
    } catch (error) {
      console.error('Error analyzing cart for preorder message:', error);
      hideMessage();
    }
  }

  /**
   * Check current cart and update message
   */
  async function checkCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cart = await response.json();
      await updateMessage(cart);
    } catch (error) {
      console.error('Error checking cart:', error);
    }
  }

  /**
   * Initialize
   */
  function init() {
    console.log('🚀 Initializing CartPreorderMessage');

    const wasDisabled = sessionStorage.getItem('preorderCheckoutDisabled') === 'true';
    const alreadyDisabledButtons = document.querySelectorAll('[data-preorder-disabled="true"]');
    const hasServerMessage = !!document.querySelector('.cart-preorder-message');
    const hasServerMessage2 = !!document.querySelector('.cart-preorder2-message');

    if (wasDisabled || alreadyDisabledButtons.length > 0 || hasServerMessage || hasServerMessage2) {
      console.log('⚡ Found previously disabled state - checking current cart to confirm');
      checkCart();
      return;
    }

    const serverData = getServerPreorderData();
    if (serverData) {
      console.log('✅ Server preorder data loaded');

      if (serverData.hasPreorder && serverData.hasRegular) {
        console.log('⚡ Server detected preorder mix - showing message and disabling checkout');
        showMessage();
      } else if (serverData.hasPreorder2 && serverData.hasRegular && !serverData.hasPreorder) {
        console.log('⚡ Server detected preorder2 mix - showing info message');
        showPreorder2Message();
      } else {
        updateCheckoutButtons(false);
      }
    } else {
      console.log('📡 No server data, checking current cart state...');
      checkCart();
    }
  }

  /**
   * Clear product cache
   */
  function clearCache() {
    productCache.clear();
    preorderHandleCache.clear();
    preorder2HandleCache.clear();
    console.log('🔄 All caches cleared');
  }

  return {
    init,
    updateMessage,
    checkCart,
    showMessage,
    hideMessage,
    clearCache,
    updateCheckoutButtons
  };
})();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.CartPreorderMessage.init();
  });
} else {
  window.CartPreorderMessage.init();
}

// Debug helper
window.debugPreorder = {
  init: () => window.CartPreorderMessage.init(),
  check: () => window.CartPreorderMessage.checkCart(),
  show: () => window.CartPreorderMessage.showMessage(),
  hide: () => window.CartPreorderMessage.hideMessage(),
  clearCache: () => window.CartPreorderMessage.clearCache(),
  enableCheckout: () => window.CartPreorderMessage.updateCheckoutButtons(false),
  disableCheckout: () => window.CartPreorderMessage.updateCheckoutButtons(true),
  status: () => {
    const checkoutButtons = document.querySelectorAll('a[href="/checkout"], button[name="checkout"], a[data-preorder-disabled="true"]');
    const disabledButtons = document.querySelectorAll('[data-preorder-disabled="true"]');

    console.log('Status:', {
      scriptLoaded: !!window.CartPreorderMessage,
      container: !!document.getElementById('cart-preorder-message-container'),
      serverData: !!document.getElementById('cart-preorder-data'),
      messageVisible: !!document.querySelector('.cart-preorder-message'),
      preorder2MessageVisible: !!document.querySelector('.cart-preorder2-message'),
      checkoutButtonsFound: checkoutButtons.length,
      checkoutButtonsDisabled: disabledButtons.length,
      cacheSize: 'N/A (private)'
    });
  }
};

console.log('✅ CartPreorderMessage initialized');
console.log('💡 Debug: window.debugPreorder.check()');
